import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Select from 'react-select';
import { useMutation } from 'react-apollo';
import { UPSERT_SPECIALIZATION_MAPPING } from '../queries';
import { toast } from 'react-toastify';

const AddEditMappingModal = ({ isOpen, toggle, isEdit, mapping, mappings }) => {
  const specializationOptions = [
    ...new Set(mappings.map(m => m.specialization))
  ].map(value => ({
    label: value,
    value
  }));

  const baseCategoryOptions = [...new Set(mappings.map(m => m.specialty))].map(
    value => ({
      label: value,
      value
    })
  );

  const categoryOptions = isEdit
    ? baseCategoryOptions
    : [...baseCategoryOptions, { label: 'Add New', value: '__add_new__' }];

  const [selectedSpecialization, setSelectedSpecialization] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    if (mapping) {
      setSelectedSpecialization({
        label: mapping.specialization,
        value: mapping.specialization
      });

      setSelectedCategory({
        label: mapping.specialty,
        value: mapping.specialty
      });
    } else {
      setSelectedSpecialization(null);
      setSelectedCategory(null);
      setNewCategory('');
    }
  }, [mapping, isOpen]);

  const showNewCategoryInput =
    selectedCategory && selectedCategory.value === '__add_new__';

  const [upsertMapping, { loading }] = useMutation(
    UPSERT_SPECIALIZATION_MAPPING,
    {
      refetchQueries: ['GET_SPECIALIZATION_MAPPING'],

      onCompleted: () => {
        toast.success(
          isEdit
            ? 'Mapping updated successfully'
            : 'Mapping created successfully'
        );
      },

      onError: () => {
        toast.error('Something went wrong!');
      }
    }
  );

  const handleSave = async () => {
    const category =
      selectedCategory && selectedCategory.value === '__add_new__'
        ? newCategory
        : selectedCategory && selectedCategory.value;

    if (!selectedSpecialization || !category) return;

    await upsertMapping({
      variables: {
        input: [
          {
            specialization: selectedSpecialization.value,
            specialty: category
          }
        ]
      }
    });

    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered>
      <ModalHeader toggle={toggle}>
        {isEdit ? 'Edit Mapping' : 'Add Mapping'}
      </ModalHeader>

      <ModalBody>
        <div className="mapping-form">
          <div className="mapping-form-row">
            <label>AMS Specialty:</label>

            <div className="mapping-input">
              <Select
                options={specializationOptions}
                value={selectedSpecialization}
                onChange={setSelectedSpecialization}
                className="mapping-select"
              />
            </div>
          </div>

          <div className="mapping-form-row">
            <label>Category:</label>

            <div className="mapping-input">
              {!showNewCategoryInput && (
                <Select
                  options={categoryOptions}
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  className="mapping-select"
                />
              )}

              {showNewCategoryInput && (
                <input
                  type="text"
                  className="form-control mapping-text-input"
                  placeholder="Enter new category"
                  value={newCategory}
                  onChange={e => setNewCategory(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>
      </ModalBody>

      <ModalFooter className="mapping-modal-footer">
        <Button
          className="mapping-save-btn"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>

        <Button className="mapping-cancel-btn" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditMappingModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  isEdit: PropTypes.bool,
  mapping: PropTypes.object,
  mappings: PropTypes.array
};

export default AddEditMappingModal;
